import { get } from './api';

// Types for analytics data
export interface DealershipPrice {
  dealership: string;
  totalPrice: number;
  averagePrice: number;
  reportCount: number;
}

export interface DealershipStats {
  dealership: string;
  reportCount: number;
  uniqueUsers: number;
}

export interface VehicleStats {
  make: string;
  model: string;
  count: number;
}

export interface StateStats {
  state: string;
  reportCount: number;
  uniqueUsers: number;
  totalDealerships: number;
}

export interface AnalyticsData {
  dealershipPrices: DealershipPrice[];
  dealershipReportCounts: DealershipStats[];
  dealershipUserCounts: DealershipStats[];
  vehicleMakes: VehicleStats[];
  vehicleModels: VehicleStats[];
  stateStats: StateStats[];
  totalReports: number;
  totalUsers: number;
  totalDealerships: number;
  dateRange: {
    start: string;
    end: string;
  };
}

export interface RawReport {
  user_id: string;
  dealership_name: string;
  report_content: {
    strategic_analysis?: {
      key_financial_data?: {
        current_price?: string;
      };
    };
  };
  vehicle_info: {
    make?: string;
    model?: string;
  };
  title: string;
  created_at: any;
  // Location fields
  dealership_address?: string;
  dealership_location?: string;
  location?: string;
  address?: string;
  zip_code?: string;
  state?: string;
  city?: string;
}

// Utility functions for data processing
export const extractPriceFromString = (priceStr: string): number => {
  if (!priceStr || typeof priceStr !== 'string') {
    return 0;
  }
  
  // Handle common price formats
  // Examples: "$65,000", "$43,000-$179,900", "Varies by model ($43,000-$179,900)"
  
  // Remove currency symbols and extra text, keep numbers, commas, periods, and dashes
  let cleanStr = priceStr.replace(/[^0-9,.-]/g, '');
  
  // Handle ranges like "43,000-179,900" - take the lower bound
  if (cleanStr.includes('-')) {
    cleanStr = cleanStr.split('-')[0];
  }
  
  // Remove commas (for thousands separators)
  cleanStr = cleanStr.replace(/,/g, '');
  
  // Convert to number
  const price = parseFloat(cleanStr);
  
  return isNaN(price) ? 0 : price;
};

export const extractStateFromReport = (report: RawReport): string => {
  // Try to extract state from various fields
  
  // 1. Direct state field
  if (report.state && report.state.trim()) {
    return report.state.trim().toUpperCase();
  }
  
  // 2. Extract from dealership name (some dealerships include location)
  const dealershipName = report.dealership_name || '';
  
  // Common patterns in dealership names
  const statePatterns = [
    // "Dealership of City, ST" or "Dealership City ST"
    /,\s*([A-Z]{2})$/,
    /\s([A-Z]{2})$/,
    // "Dealership - City, ST" 
    /-\s*[^,]+,\s*([A-Z]{2})$/,
    // "City ST Dealership"
    /^[^A-Z]*([A-Z]{2})\s+/,
  ];
  
  for (const pattern of statePatterns) {
    const match = dealershipName.match(pattern);
    if (match && match[1]) {
      return match[1];
    }
  }
  
  // 3. Extract from address fields
  const addressFields = [
    report.dealership_address,
    report.address,
    report.dealership_location,
    report.location
  ].filter(Boolean);
  
  for (const address of addressFields) {
    if (address) {
      // Look for state abbreviation in address (last 2 letters after comma, or zip code pattern)
      const stateMatch = address.match(/,\s*([A-Z]{2})\s*\d{5}?/);
      if (stateMatch) {
        return stateMatch[1];
      }
      // Just state abbreviation
      const simpleStateMatch = address.match(/\b([A-Z]{2})\b/);
      if (simpleStateMatch) {
        return simpleStateMatch[1];
      }
    }
  }
  
  // 4. Extract from dealership name with city names (common patterns)
  const cityStatePatterns = [
    { pattern: /Manhattan|NYC|Brooklyn|Queens|Bronx/, state: 'NY' },
    { pattern: /Los Angeles|Beverly Hills|Hollywood|Pasadena/, state: 'CA' },
    { pattern: /Miami|Orlando|Tampa|Jacksonville/, state: 'FL' },
    { pattern: /Dallas|Houston|Austin|San Antonio/, state: 'TX' },
    { pattern: /Chicago|Naperville|Schaumburg/, state: 'IL' },
    { pattern: /Atlanta|Savannah|Augusta/, state: 'GA' },
    { pattern: /Phoenix|Scottsdale|Tucson/, state: 'AZ' },
    { pattern: /Denver|Boulder|Colorado Springs/, state: 'CO' },
    { pattern: /Seattle|Tacoma|Bellevue/, state: 'WA' },
    { pattern: /Portland|Eugene/, state: 'OR' },
    { pattern: /Las Vegas|Reno/, state: 'NV' },
    { pattern: /Boston|Cambridge|Worcester/, state: 'MA' },
    { pattern: /Philadelphia|Pittsburgh|Allentown/, state: 'PA' },
    { pattern: /Detroit|Grand Rapids|Ann Arbor/, state: 'MI' },
    { pattern: /Cleveland|Columbus|Cincinnati/, state: 'OH' },
    { pattern: /Baltimore|Annapolis/, state: 'MD' },
    { pattern: /Virginia Beach|Norfolk|Richmond/, state: 'VA' },
    { pattern: /Charlotte|Raleigh|Durham/, state: 'NC' },
    { pattern: /Nashville|Memphis|Knoxville/, state: 'TN' },
    { pattern: /Louisville|Lexington/, state: 'KY' },
    { pattern: /Indianapolis|Fort Wayne/, state: 'IN' },
    { pattern: /Milwaukee|Madison|Green Bay/, state: 'WI' },
    { pattern: /Minneapolis|Saint Paul|Rochester/, state: 'MN' },
    { pattern: /Kansas City|Topeka/, state: 'KS' },
    { pattern: /Oklahoma City|Tulsa/, state: 'OK' },
    { pattern: /Little Rock|Fayetteville/, state: 'AR' },
    { pattern: /New Orleans|Baton Rouge/, state: 'LA' },
    { pattern: /Jackson|Biloxi/, state: 'MS' },
    { pattern: /Birmingham|Montgomery|Mobile/, state: 'AL' },
    { pattern: /Salt Lake City|Provo/, state: 'UT' },
    { pattern: /Boise|Nampa/, state: 'ID' },
    { pattern: /Chantilly|Tysons|Arlington|Alexandria/, state: 'VA' },
    { pattern: /Princeton|Trenton/, state: 'NJ' },
    { pattern: /Rockville|Bethesda/, state: 'MD' },
  ];
  
  for (const { pattern, state } of cityStatePatterns) {
    if (pattern.test(dealershipName)) {
      return state;
    }
  }
  
  return 'UNKNOWN';
};

export const extractVehicleData = (report: RawReport): { make: string; model: string } => {
  let make = '';
  let model = '';
  
  // Try vehicle_info first
  if (report.vehicle_info && typeof report.vehicle_info === 'object') {
    make = report.vehicle_info.make || '';
    model = report.vehicle_info.model || '';
  }
  
  // If not found, try to extract from title
  if (!make || !model) {
    const title = report.title || '';
    if (title) {
      // Common pattern: "2022 Ford Expedition Max Limited - Purchase Analysis Report"
      const parts = title.includes(' - ') ? title.split(' - ')[0] : title;
      const words = parts.split(' ');
      
      // Skip year if present at the beginning
      let startIndex = 0;
      if (words.length > 0 && /^\d{4}$/.test(words[0])) {
        startIndex = 1;
      }
      
      if (words.length > startIndex + 1) {
        if (!make) {
          make = words[startIndex];
        }
        if (!model && words.length > startIndex + 1) {
          model = words[startIndex + 1];
        }
      }
    }
  }
  
  return { make, model };
};

export const formatPrice = (value: number): string => {
  if (value >= 1000000) {
    return `$${(value / 1000000).toFixed(1)}M`;
  } else if (value >= 1000) {
    return `$${(value / 1000).toFixed(0)}K`;
  } else {
    return `$${value.toFixed(0)}`;
  }
};

// Helper function to check if a value should be excluded
const isDefaultValue = (value: string, excludedValues: string[]): boolean => {
  if (!value || value.trim() === '') return true;
  const normalizedValue = value.trim().toLowerCase();
  return excludedValues.some(excluded => excluded.toLowerCase() === normalizedValue);
};

export const processAnalyticsData = (reports: RawReport[]): AnalyticsData => {
  // Excluded users
  const excludedUsers = ['zbVAdNs8iYYPREDqZpWy1OXTsdO2', 'Gcj1i7d0zmYew1t1WBZTkuYpl473'];
  
  // Default values to filter out (more comprehensive list)
  const excludedDealerships = ['Unknown', 'unknown', 'UNKNOWN', 'Unknown Dealership', 'unknown dealership', ''];
  const excludedMakes = ['Car', 'car', 'CAR', 'Vehicle', 'vehicle', 'VEHICLE', ''];
  const excludedModels = [
    'Car Purchase', 'car purchase', 'CAR PURCHASE',
    'Purchase', 'purchase', 'PURCHASE', 
    'Car', 'car', 'CAR',
    'Vehicle', 'vehicle', 'VEHICLE',
    'Model', 'model', 'MODEL',
    ''
  ];
  
  // Filter out excluded users
  const filteredReports = reports.filter(report => !excludedUsers.includes(report.user_id));
  
  // Process dealership prices
  const dealershipPriceMap = new Map<string, { prices: number[]; users: Set<string> }>();
  
  // Process vehicle counts
  const makeCountMap = new Map<string, number>();
  const modelCountMap = new Map<string, number>();
  
  // Process state analytics
  const stateStatsMap = new Map<string, { reportCount: number; users: Set<string>; dealerships: Set<string> }>();
  
  // Process reports
  filteredReports.forEach((report, index) => {
    const dealershipName = report.dealership_name || '';
    const userId = report.user_id || '';
    
    // Skip excluded dealerships using robust checking
    if (isDefaultValue(dealershipName, excludedDealerships)) {
      return;
    }
    
    // Initialize dealership data if not exists
    if (!dealershipPriceMap.has(dealershipName)) {
      dealershipPriceMap.set(dealershipName, { prices: [], users: new Set() });
    }
    
    const dealershipData = dealershipPriceMap.get(dealershipName)!;
    
    // Add user to dealership
    if (userId) {
      dealershipData.users.add(userId);
    }
    
    // Extract and add price
    const priceStr = report.report_content?.strategic_analysis?.key_financial_data?.current_price || '';
    if (priceStr) {
      const price = extractPriceFromString(priceStr);
      if (price > 0) {
        dealershipData.prices.push(price);
        if (index < 10) { // Debug first 10 reports to see dealership data structure
          // Location data analysis could be added here if needed
        }
      }
    }
    
    // Process vehicle data
    const { make, model } = extractVehicleData(report);
    
    // Add make if not excluded (using robust checking)
    if (make && !isDefaultValue(make, excludedMakes)) {
      makeCountMap.set(make, (makeCountMap.get(make) || 0) + 1);
    }
    
    // Add model if not excluded (using robust checking)
    if (model && !isDefaultValue(model, excludedModels)) {
      const validMake = make && !isDefaultValue(make, excludedMakes);
      const fullModel = validMake ? `${make} ${model}` : model;
      modelCountMap.set(fullModel, (modelCountMap.get(fullModel) || 0) + 1);
    }
    
    // Process state data
    const state = extractStateFromReport(report);
    if (state && state !== 'UNKNOWN') {
      if (!stateStatsMap.has(state)) {
        stateStatsMap.set(state, { reportCount: 0, users: new Set(), dealerships: new Set() });
      }
      
      const stateData = stateStatsMap.get(state)!;
      stateData.reportCount++;
      
      if (userId) {
        stateData.users.add(userId);
      }
      
      if (dealershipName && !isDefaultValue(dealershipName, excludedDealerships)) {
        stateData.dealerships.add(dealershipName);
      }
    }
  });
  
  // Convert to arrays and sort
  // Check Honda of Princeton specifically
  
  
  const dealershipPrices: DealershipPrice[] = Array.from(dealershipPriceMap.entries())
    .map(([dealership, data]) => ({
      dealership,
      totalPrice: data.prices.reduce((sum, price) => sum + price, 0),
      averagePrice: data.prices.length > 0 ? data.prices.reduce((sum, price) => sum + price, 0) / data.prices.length : 0,
      reportCount: data.prices.length
    }))
    .filter(item => item.totalPrice > 0)
    .sort((a, b) => b.totalPrice - a.totalPrice);
    
  
  const dealershipReportCounts: DealershipStats[] = Array.from(dealershipPriceMap.entries())
    .map(([dealership, data]) => ({
      dealership,
      reportCount: filteredReports.filter(r => {
        const dealershipName = r.dealership_name || '';
        return dealershipName === dealership && !isDefaultValue(dealershipName, excludedDealerships);
      }).length,
      uniqueUsers: data.users.size
    }))
    .filter(item => item.reportCount > 0)
    .sort((a, b) => b.reportCount - a.reportCount);
  
  const dealershipUserCounts: DealershipStats[] = Array.from(dealershipPriceMap.entries())
    .map(([dealership, data]) => ({
      dealership,
      reportCount: filteredReports.filter(r => {
        const dealershipName = r.dealership_name || '';
        return dealershipName === dealership && !isDefaultValue(dealershipName, excludedDealerships);
      }).length,
      uniqueUsers: data.users.size
    }))
    .filter(item => item.uniqueUsers > 0)
    .sort((a, b) => b.uniqueUsers - a.uniqueUsers);
  
  const vehicleMakes: VehicleStats[] = Array.from(makeCountMap.entries())
    .map(([make, count]) => ({ make, model: '', count }))
    .sort((a, b) => b.count - a.count);
  
  const vehicleModels: VehicleStats[] = Array.from(modelCountMap.entries())
    .map(([fullModel, count]) => {
      const parts = fullModel.split(' ');
      return {
        make: parts[0] || '',
        model: fullModel,
        count
      };
    })
    .sort((a, b) => b.count - a.count);
  
  // Process state statistics
  const stateStats: StateStats[] = Array.from(stateStatsMap.entries())
    .map(([state, data]) => ({
      state,
      reportCount: data.reportCount,
      uniqueUsers: data.users.size,
      totalDealerships: data.dealerships.size
    }))
    .sort((a, b) => b.reportCount - a.reportCount);
  
  
  // Calculate summary stats
  const totalReports = filteredReports.length;
  const totalUsers = new Set(filteredReports.map(r => r.user_id)).size;
  const totalDealerships = dealershipPriceMap.size;
  
  // Calculate date range
  const dates = filteredReports
    .map(r => r.created_at)
    .filter(date => date)
    .sort();
  
  const dateRange = {
    start: dates.length > 0 ? dates[0] : '',
    end: dates.length > 0 ? dates[dates.length - 1] : ''
  };
  
  return {
    dealershipPrices,
    dealershipReportCounts,
    dealershipUserCounts,
    vehicleMakes,
    vehicleModels,
    stateStats,
    totalReports,
    totalUsers,
    totalDealerships,
    dateRange
  };
};

// API service functions
export const analyticsService = {
  // Get analytics data
  async getAnalytics(): Promise<AnalyticsData> {
    try {
      const response = await get<{ reports: RawReport[] }>('/analytics/reports');
      return processAnalyticsData(response.reports);
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      throw error;
    }
  },

  // Get analytics summary
  async getSummary(): Promise<any> {
    try {
      return await get('/analytics/summary');
    } catch (error) {
      console.error('Error fetching analytics summary:', error);
      throw error;
    }
  }
};

export default analyticsService;